import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CONTRACT_TEMPLATES } from "./contracts";
import { companyCompliance } from "./model";

const emptyState = { version: 4, companies: {}, projects: {}, audit: [] };

export const useBackendEngineState = () => {
  const { user } = useAuth();
  const [state, setState] = useState(emptyState);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [trackedUser, setTrackedUser] = useState(user);
  const initializedRef = useRef(false);

  // L'identité de `user` change (ex: null → utilisateur réel juste après connexion) dans le même
  // rendu que celui qui affiche déjà l'espace protégé : on réinitialise loading/ready pendant le
  // rendu (avant peinture) pour éviter un flash avec des données vides qui provoquerait une
  // redirection prématurée (voir Phase3Gate / ClientProjectPage).
  if (trackedUser !== user) {
    setTrackedUser(user);
    setLoading(true);
    setReady(false);
    initializedRef.current = false;
  }

  const refresh = useCallback(async () => {
    if (!user) {
      setLoading(false);
      setReady(true);
      return;
    }
    // Seul le premier chargement affiche l'écran de chargement plein écran (Phase3Gate) :
    // les rafraîchissements déclenchés par une mutation (fire()) restent silencieux pour ne pas
    // démonter/remonter les pages (et perdre l'onglet actif en cours de consultation).
    if (!initializedRef.current) {
      setLoading(true);
      setReady(false);
    }
    try {
      const list = (await api.get("/phase3/projects")).data;
      const projects = await Promise.all(list.map((p) => api.get(`/phase3/projects/${p.id}`).then((r) => r.data)));
      const companies = {};
      const byRef = {};
      projects.forEach((p) => {
        Object.assign(companies, p.companies || {});
        byRef[p.ref] = p;
      });
      let audit = [];
      const partnerCanAudit = user.partner_membership?.internal_role === "OWNER" || user.partner_membership?.permissions?.includes("projects.edit");
      if (user.role === "CENTRAL" || (user.role === "PARTNER" && partnerCanAudit)) {
        const rows = await Promise.all(projects.map((p) => api.get(`/phase3/projects/${p.id}/audit`).then((r) =>
          r.data.map((e) => ({ ...e, project_id: p.ref, role: String(e.role || "").toLowerCase() }))
        )));
        audit = rows.flat().sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      }
      setState({ version: 4, companies, projects: byRef, audit });
      setError("");
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
      setReady(true);
      initializedRef.current = true;
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const fire = useCallback((request) => {
    request.then(refresh).catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)));
    return true;
  }, [refresh]);

  return useMemo(() => {
    const project = (ref) => state.projects[ref];
    const company = (id) => state.companies[id];
    const contract = (ref, id) => project(ref)?.contracts.find((c) => c.contract_id === id);
    const quote = (ref, id) => project(ref)?.quotes.find((q) => q.id === id);
    const invoice = (ref, id) => project(ref)?.invoices.find((i) => i.id === id);
    const lot = (ref, id) => project(ref)?.lots.find((l) => l.id === id);
    return {
      state, mode: "BACKEND", user, backendLoading: loading, backendReady: ready, backendError: error,
      currentEntityId: user?.entity_id, refresh, reset: refresh,
      project, company,
      compliance: (id) => company(id) ? companyCompliance(company(id)) : { ok: false, missing: [] },
      templates: CONTRACT_TEMPLATES,
      uploadPrivateFile: async (ref, file, entityType, entityId, visibility = "PARTNER,COMPANY") => {
        const form = new FormData();
        form.append("file", file); form.append("project_id", ref); form.append("entity_type", entityType);
        form.append("entity_id", entityId); form.append("visibility", visibility);
        return (await api.post("/phase3/documents", form)).data;
      },
      addProjectDocument: async (ref, file, title, category, visibleToClient) => {
        const form = new FormData();
        form.append("file", file); form.append("project_id", ref); form.append("entity_type", category);
        form.append("entity_id", project(ref)?.id || ref); form.append("title", title);
        form.append("visibility", visibleToClient ? "PARTNER,CLIENT" : "PARTNER");
        const data = (await api.post("/phase3/documents", form)).data;
        await refresh();
        return data;
      },
      uploadCompanyDocument: async (file, documentType, validFrom, validTo) => {
        const form = new FormData();
        form.append("file", file); form.append("document_type", documentType);
        form.append("valid_from", validFrom); form.append("valid_to", validTo);
        const data = (await api.post("/phase3/companies/me/documents", form)).data;
        await refresh();
        return data;
      },
      depositQuote: (ref, companyId, data) => data.fileId
        ? fire(api.post(`/phase3/projects/${ref}/quotes`, {
          lot_id: data.lotId, file_id: data.fileId, number: data.number, date: data.date,
          amount_ht: Number(data.amountHT), tva: Number(data.tva || 20), duration_days: Number(data.durationDays),
          headcount: Number(data.headcount || 1), available_from: data.availableFrom || null,
          constraints: data.comment || "", prerequisites: data.prerequisites || "",
        }))
        : (toast.error("Un PDF réellement téléversé est obligatoire en mode BACKEND."), false),
      setQuoteStatus: (ref, id, status) => fire(api.patch(`/phase3/projects/${ref}/quotes/${id}`, { status, version: quote(ref, id)?.version || 1 })),
      catalog: async () => (await api.get("/phase3/catalog")).data,
      companiesDirectory: async (q, trade) => (await api.get("/phase3/companies", { params: { q, trade } })).data,
      createCompany: async (name, trade, email, phone, siret) => {
        const data = (await api.post("/phase3/companies", { name, trade, email: email || null, phone: phone || null, siret: siret || null })).data;
        await refresh();
        return data;
      },
      attachCompanyToProject: (ref, companyId, origin) => fire(api.post(`/phase3/projects/${ref}/companies`, { company_id: companyId, origin })),
      setCompanyCommission: (ref, companyId, excluded) => fire(api.patch(`/phase3/projects/${ref}/companies/${companyId}`, { commission_excluded: excluded })),
      requestQuotes: (ref, catalogCode, companyIds, comment) => fire(api.post(`/phase3/projects/${ref}/quote-requests`, {
        catalog_code: catalogCode, company_ids: companyIds, comment: comment || "",
      })),
      quoteRequestsForProject: async (ref) => (await api.get(`/phase3/projects/${ref}/quote-requests`)).data,
      financialSummary: async (ref) => (await api.get(`/phase3/projects/${ref}/financial-summary`)).data,
      setProjectFeeFormula: (ref, feeFormula) => fire(api.patch(`/phase3/projects/${ref}`, {
        fee_formula: feeFormula, version: project(ref)?.version || 1,
      })),
      transitionPhase: (ref, direction) => fire(api.post(`/phase3/projects/${ref}/transition-phase`, {
        direction, version: project(ref)?.version || 1,
      })),
      addProjectNote: (ref, title, text) => fire(api.post(`/phase3/projects/${ref}/notes`, { title, text })),
      companyQuoteRequests: async () => (await api.get("/phase3/company/quote-requests")).data,
      respondQuoteRequest: (requestId, payload) => fire(api.post(`/phase3/company/quote-requests/${requestId}/respond`, payload)),
      companyNotifications: async () => (await api.get("/phase3/company/notifications")).data,
      markCompanyNotificationRead: (id) => fire(api.patch(`/phase3/company/notifications/${id}/read`)),
      moveLot: (ref, id, start, end) => fire(api.patch(`/phase3/projects/${ref}/planning/${id}`, {
        start, end, dependencies: lot(ref, id)?.dependsOn || [], version: lot(ref, id)?.planningVersion || 1,
      })),
      declareProgress: (ref, companyId, lotId, declared, comment, photos) => fire(api.post(`/phase3/projects/${ref}/progress`, {
        lot_id: lotId, declared_progress: declared, comment, photo_ids: [],
      })),
      reviewProgress: (ref, id, decision, value, comment) => fire(api.patch(`/phase3/projects/${ref}/progress/${id}`, {
        decision: decision === "refuse" ? "refusee" : decision === "propose" ? "corrigee" : "validee",
        validated_progress: decision === "refuse" ? null : value, comment,
      })),
      signalObservation: (ref, role, title, comment, lotId, photoIds = [], kind = "signalement") => fire(api.post(`/phase3/projects/${ref}/observations`, {
        title, comment, lot_id: lotId || null, photo_ids: photoIds, kind,
      })),
      reportObservation: (ref, role, obsId, comment) => fire(api.patch(`/phase3/projects/${ref}/observations/${obsId}/report`, {
        comment, version: project(ref)?.observations.find((o) => o.id === obsId)?.version || 1,
      })),
      assignObservation: (ref, id, lotId) => fire(api.patch(`/phase3/projects/${ref}/observations/${id}/assign`, {
        lot_id: lotId, version: project(ref)?.observations.find((o) => o.id === id)?.version || 1,
      })),
      treatObservation: (ref, companyId, id, done, afterPhotoIds = []) => fire(api.patch(`/phase3/projects/${ref}/observations/${id}`, {
        status: done ? "a_verifier" : "en_cours", comment: done ? "Réalisé" : "Pris en charge", photo_ids: afterPhotoIds,
        version: project(ref)?.observations.find((o) => o.id === id)?.version || 1,
      })),
      closeObservation: (ref, id, ok) => fire(api.patch(`/phase3/projects/${ref}/observations/${id}`, {
        status: ok ? "cloture" : "a_traiter", comment: ok ? "Vérifié et clôturé" : "Correction demandée",
        version: project(ref)?.observations.find((o) => o.id === id)?.version || 1,
      })),
      depositInvoice: (ref, companyId, data) => data.fileId
        ? fire(api.post(`/phase3/projects/${ref}/invoices`, {
          lot_id: data.lotId, file_id: data.fileId, invoice_type: data.type, number: data.number,
          date: data.date, amount_ht: Number(data.amountHT), tva: 20, comment: data.comment || "",
          corrects_invoice_id: data.correctsInvoiceId || null, correction_reason: data.correctionReason || null,
        }))
        : (toast.error("Un PDF réellement téléversé est obligatoire en mode BACKEND."), false),
      reviewInvoice: (ref, id, accepted, reason) => fire(api.patch(`/phase3/projects/${ref}/invoices/${id}/review`, {
        accepted, reason: reason || null, version: invoice(ref, id)?.recordVersion || 1,
      })),
      declarePayment: (ref, id, date, mode, proof) => fire(api.post(`/phase3/projects/${ref}/invoices/${id}/payment-declaration`, {
        date, mode, proof_file_id: proof || null,
      })),
      contestInvoice: (ref, id, reason, comment) => fire(api.post(`/phase3/projects/${ref}/invoices/${id}/contest`, { reason, comment })),
      confirmPayment: (ref, companyId, id) => fire(api.post(`/phase3/projects/${ref}/invoices/${id}/payment-confirmation`)),
      uploadDocument: () => (toast.error("Utilisez le sélecteur de fichier sécurisé en mode BACKEND."), false),
      verifyDocument: () => (toast.info("La revue Central reste accessible par l’API Phase 3."), false),
      advanceService: () => (toast.info("CozyHome Central complet n’est pas inclus dans cette phase."), false),
      createContract: (ref, templateId, parentId) => fire(api.post(`/phase3/projects/${ref}/contracts`, {
        template_id: templateId, parent_contract_id: parentId || null, fields: {},
      })),
      updateContractFields: (ref, id, fields) => fire(api.patch(`/phase3/projects/${ref}/contracts/${id}`, {
        fields, version: contract(ref, id)?.version || 1,
      })),
      setContractStatus: (ref, id, status) => fire(api.patch(`/phase3/projects/${ref}/contracts/${id}/status`, {
        status: status === "envoye" ? "en_attente" : status,
        version: contract(ref, id)?.recordVersion || 1, reason: "",
      })),
      signContract: (ref, id, accept, reason) => fire(api.patch(`/phase3/projects/${ref}/contracts/${id}/status`, {
        status: accept ? "signe" : "refuse", version: contract(ref, id)?.recordVersion || 1, reason: reason || "",
      })),
      postMessage: (ref, role, text) => fire(api.post(`/phase3/projects/${ref}/messages`, { text })),
    };
  }, [state, user, loading, ready, error, refresh, fire]);
};